/**
 * Conexão SSE única por aba. Cada evento invalida as consultas afetadas; o
 * React Query busca de novo só o que está na tela. Reconecta sozinho (o
 * EventSource já faz isso) e, ao reconectar, revalida tudo.
 *
 * Avisos discretos: a vendedora recebe um toast quando um atendimento chega
 * para ela; a gerência, quando surge um alerta de espera.
 */
import type { ConversaResumo, EventoTempoReal } from "@ydchat/shared";
import { veOperacaoInteira } from "@ydchat/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { chaveSessao, useUsuario } from "./sessao";

export type EstadoConexao = "conectando" | "online" | "offline";

const TIPOS = [
  "mensagem.nova",
  "mensagem.status",
  "conversa.atualizada",
  "usuario.presenca",
  "alerta.novo",
  "aviso.pessoal",
  "sugestoes.novas",
  "visao.atualizada",
] as const;

export function useTempoReal(): EstadoConexao {
  const qc = useQueryClient();
  const usuario = useUsuario();
  const navegar = useNavigate();
  const [estado, setEstado] = useState<EstadoConexao>("conectando");
  // Referências estáveis: o efeito não reconecta quando o usuário muda de status.
  const ref = useRef({ usuario, navegar });
  ref.current = { usuario, navegar };

  useEffect(() => {
    const es = new EventSource("/api/eventos");
    let jaConectou = false;

    const avisarNovoAtendimento = async (conversaId: string) => {
      const c = await api.get<ConversaResumo>(`/conversas/${conversaId}`).catch(() => null);
      toast(`Novo atendimento: ${c?.contato.nome ?? "cliente"}`, {
        description: c?.ultimaMensagem?.texto,
        action: {
          label: "Abrir",
          onClick: () =>
            void ref.current.navegar({ to: "/atendimentos/$conversaId", params: { conversaId } }),
        },
      });
    };

    const aplicar = (e: EventoTempoReal) => {
      const eu = ref.current.usuario;
      if ("conversaId" in e) {
        void qc.invalidateQueries({ queryKey: ["conversas"] });
        void qc.invalidateQueries({ queryKey: ["conversa", e.conversaId] });
      }
      if (e.tipo === "conversa.atualizada") {
        void qc.invalidateQueries({ queryKey: ["contexto"] });
        void qc.invalidateQueries({ queryKey: ["usuarios"] });
        void qc.invalidateQueries({ queryKey: ["alertas"] });
        const chegouParaMim =
          e.responsavelId === eu.id && e.anteriorId !== undefined && e.anteriorId !== eu.id;
        if (chegouParaMim && !veOperacaoInteira(eu.papel)) void avisarNovoAtendimento(e.conversaId);
      }
      if (e.tipo === "usuario.presenca") {
        void qc.invalidateQueries({ queryKey: ["usuarios"] });
        if (e.usuarioId === eu.id) void qc.invalidateQueries({ queryKey: chaveSessao });
      }
      if (e.tipo === "aviso.pessoal") {
        void qc.invalidateQueries({ queryKey: ["conversas"] });
        toast(e.titulo, {
          description: e.texto ?? undefined,
          duration: 15_000,
          action: {
            label: "Abrir",
            onClick: () =>
              void ref.current.navegar({
                to: "/atendimentos/$conversaId",
                params: { conversaId: e.conversaId },
              }),
          },
        });
      }
      if (e.tipo === "alerta.novo") {
        void qc.invalidateQueries({ queryKey: ["alertas"] });
        toast.warning("Cliente esperando além do limite", {
          action: { label: "Ver", onClick: () => void ref.current.navegar({ to: "/equipe" }) },
        });
      }
    };

    es.addEventListener("pronto", () => {
      setEstado("online");
      if (jaConectou) void qc.invalidateQueries();
      jaConectou = true;
    });
    for (const tipo of TIPOS) {
      es.addEventListener(tipo, (ev) =>
        aplicar(JSON.parse((ev as MessageEvent<string>).data) as EventoTempoReal),
      );
    }
    es.onerror = () => setEstado("offline");
    return () => es.close();
  }, [qc]);

  return estado;
}
